/** QuickHouse — Barre des étages : sélection, ajout, hauteur, visibilité 3D, vue éclatée */
import { useProjectStore } from '../store/useProjectStore.js';
import { createEmptyFloor } from '../../shared/schema.js';
import NumField from './NumField.jsx';
import { modelBounds } from '../viewer3d/ClipPlanes.jsx';

const CLIP_LABELS = { x: 'X', y: 'Y', z: 'H' };

// Pas de grille proposés (cm) — jusqu'au millimètre
const GRID_STEPS = [
  { value: 100, label: '1 m' },
  { value: 50, label: '50 cm' },
  { value: 25, label: '25 cm' },
  { value: 10, label: '10 cm' },
  { value: 5, label: '5 cm' },
  { value: 1, label: '1 cm' },
  { value: 0.5, label: '5 mm' },
  { value: 0.1, label: '1 mm' },
];

/** Réglage de la grille : pas (jusqu'au mm) + aimantation */
function GridControls({ project }) {
  const mutate = useProjectStore((s) => s.mutate);
  const chatBusy = useProjectStore((s) => s.chatBusy);
  const grid = project.settings.grid;
  const steps = GRID_STEPS.some((s) => s.value === grid.size)
    ? GRID_STEPS
    : [...GRID_STEPS, { value: grid.size, label: `${grid.size} cm` }].sort((a, b) => b.value - a.value);
  return (
    <>
      <span className="lbl">Grille :</span>
      <select
        value={String(grid.size)}
        disabled={chatBusy}
        title="Pas de la grille et de l'aimantation (jusqu'au millimètre)"
        onChange={(e) => mutate((fs, proj) => { proj.settings.grid.size = Number(e.target.value); })}
      >
        {steps.map((s) => <option key={s.value} value={String(s.value)}>{s.label}</option>)}
      </select>
      <label className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 3 }} title="Aimanter le tracé à la grille">
        <input
          type="checkbox"
          checked={grid.snap}
          disabled={chatBusy}
          onChange={(e) => mutate((fs, proj) => { proj.settings.grid.snap = e.target.checked; })}
        />
        Aimant
      </label>
    </>
  );
}

/** Visite virtuelle et mesure linéaire 3D */
function Walk3DControls() {
  const walkMode = useProjectStore((s) => s.walkMode);
  const setWalkMode = useProjectStore((s) => s.setWalkMode);
  const measure3d = useProjectStore((s) => s.measure3d);
  const setMeasure3d = useProjectStore((s) => s.setMeasure3d);
  const measurePoints = useProjectStore((s) => s.measurePoints);
  const clearMeasurePoints = useProjectStore((s) => s.clearMeasurePoints);

  const total = measurePoints.reduce((sum, p, i) => (i === 0 ? 0
    : sum + Math.hypot(p.x - measurePoints[i - 1].x, p.y - measurePoints[i - 1].y, p.z - measurePoints[i - 1].z)), 0);

  return (
    <>
      <button
        className={walkMode ? 'active' : ''}
        onClick={() => setWalkMode(!walkMode)}
        title="Visite virtuelle : ZQSD/WASD + flèches pour marcher, souris pour tourner la tête (clic pour capturer le pointeur), Maj = courir"
      >
        🚶 Visite
      </button>
      <button
        className={measure3d ? 'active' : ''}
        onClick={() => setMeasure3d(!measure3d)}
        title="Mesure linéaire : cliquez des points sur les surfaces (murs, sols, objets) — segments droits cumulés"
      >
        📏 Mesurer
      </button>
      {measure3d && measurePoints.length > 1 && (
        <span className="lbl">Σ {total.toFixed(2)} m</span>
      )}
      {measure3d && measurePoints.length > 0 && (
        <button onClick={clearMeasurePoints} title="Effacer la mesure en cours">⌫</button>
      )}
    </>
  );
}

/** Contrôles des plans de coupe 3D */
function ClipControls({ project }) {
  const clip = useProjectStore((s) => s.clip);
  const setClip = useProjectStore((s) => s.setClip);
  const bounds = modelBounds(project);
  return (
    <>
      <span className="lbl">Coupe :</span>
      {['x', 'y', 'z'].map((axis) => {
        const c = clip[axis];
        const [min, max] = bounds[axis];
        return (
          <span key={axis} className="floor-btn" title={`Plan de coupe ${CLIP_LABELS[axis]} — masque ce qui est au-delà (⇄ pour l'autre côté)`}>
            <label className="lbl" style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <input
                type="checkbox"
                checked={c.on}
                onChange={(e) => setClip(axis, { on: e.target.checked, value: c.value ?? Math.round((min + max) / 2) })}
              />
              {CLIP_LABELS[axis]}
            </label>
            {c.on && (
              <>
                <input
                  type="range" min={min} max={max} step="5"
                  value={c.value ?? Math.round((min + max) / 2)}
                  style={{ width: 90 }}
                  onChange={(e) => setClip(axis, { value: Number(e.target.value) })}
                />
                <button
                  className={`mini ${c.flip ? 'active' : ''}`}
                  style={{ padding: '0 5px' }}
                  title="Masquer l'autre côté"
                  onClick={() => setClip(axis, { flip: !c.flip })}
                >
                  ⇄
                </button>
              </>
            )}
          </span>
        );
      })}
    </>
  );
}

export default function FloorBar() {
  const project = useProjectStore((s) => s.project);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  const setActiveFloor = useProjectStore((s) => s.setActiveFloor);
  const viewMode = useProjectStore((s) => s.viewMode);
  const hiddenFloors = useProjectStore((s) => s.hiddenFloors);
  const toggleFloorHidden = useProjectStore((s) => s.toggleFloorHidden);
  const explode = useProjectStore((s) => s.explode);
  const setExplode = useProjectStore((s) => s.setExplode);
  const mutate = useProjectStore((s) => s.mutate);
  const chatBusy = useProjectStore((s) => s.chatBusy);
  if (!project) return null;

  const floors = [...project.floors].sort((p, q) => p.level - q.level);
  const active = project.floors.find((f) => f.id === activeFloorId);

  const addFloor = () => {
    const level = Math.max(...project.floors.map((f) => f.level)) + 1;
    const name = level === 0 ? 'RDC' : `Étage ${level}`;
    mutate((fs, proj) => {
      fs.push(createEmptyFloor(name, level, proj.settings.defaultWallHeight));
    });
  };

  // Suppression d'étage : directe s'il est vide, confirmation détaillée sinon
  const removeFloor = (id) => {
    if (project.floors.length <= 1) return;
    const f = project.floors.find((x) => x.id === id);
    if (!f) return;
    const counts = [
      [f.walls.length, 'mur(s)'],
      [f.openings.length, 'ouverture(s)'],
      [f.rooms.length, 'pièce(s)/dalle(s)/toit(s)'],
      [(f.stairs ?? []).length, 'escalier(s)'],
      [(f.items ?? []).length, 'objet(s)'],
    ].filter(([n]) => n > 0);
    if (counts.length) {
      const detail = counts.map(([n, label]) => `${n} ${label}`).join(', ');
      if (!window.confirm(`L'étage « ${f.name} » contient ${detail}.\nLe supprimer avec tout son contenu ?`)) return;
    }
    mutate((fs) => {
      const idx = fs.findIndex((x) => x.id === id);
      if (idx >= 0) fs.splice(idx, 1);
    });
    if (activeFloorId === id) setActiveFloor(project.floors.find((x) => x.id !== id)?.id);
  };

  return (
    <div className="floorbar">
      <span className="lbl">Étages :</span>
      {floors.map((f) => (
        <span key={f.id} className="floor-btn">
          <button
            className={f.id === activeFloorId ? 'active' : ''}
            onClick={() => setActiveFloor(f.id)}
            disabled={chatBusy}
          >
            {f.name}
          </button>
          {f.id === activeFloorId && floors.length > 1 && (
            <button
              className="danger"
              style={{ padding: '2px 6px', fontSize: 10 }}
              title="Supprimer cet étage (direct s'il est vide, confirmation sinon)"
              onClick={() => removeFloor(f.id)}
              disabled={chatBusy}
            >
              ✕
            </button>
          )}
          {viewMode === '3d' && (
            <span
              className={`eye ${hiddenFloors[f.id] ? '' : 'visible'}`}
              onClick={() => toggleFloorHidden(f.id)}
              title="Afficher / masquer en 3D"
            >
              👁
            </span>
          )}
        </span>
      ))}
      <button onClick={addFloor} disabled={chatBusy} title="Ajouter un étage">+</button>
      {active && viewMode === '2d' && (
        <>
          <span className="lbl">Hauteur (cm) :</span>
          <NumField
            style={{ width: 70 }}
            value={active.height}
            positive
            disabled={chatBusy}
            onCommit={(v) => mutate((fs) => { fs.find((f) => f.id === active.id).height = v; })}
          />
          <GridControls project={project} />
          <span className="lbl" title="Azimut du nord géographique : degrés depuis le haut du plan, sens horaire (0 = nord en haut)">Nord (°) :</span>
          <NumField
            style={{ width: 55 }}
            value={project.settings.north ?? 0}
            disabled={chatBusy}
            onCommit={(v) => mutate((fs, proj) => { proj.settings.north = ((v % 360) + 360) % 360; })}
          />
        </>
      )}
      {viewMode === '3d' && (
        <>
          <span className="lbl">Éclaté :</span>
          <input
            type="range" min="0" max="3" step="0.1"
            value={explode}
            onChange={(e) => setExplode(Number(e.target.value))}
          />
          <ClipControls project={project} />
          <Walk3DControls />
        </>
      )}
    </div>
  );
}
